import React, { useEffect, useState } from "react"
import { Col, Button, Spinner, ListGroup, ListGroupItem } from "react-bootstrap"
import { InputField, Page } from "@src/components"
import { usePartner } from "@src/partner"
import { useFleets } from "./useFleets"
import { useTranslation } from "react-i18next"
import { ModalView } from "@src/components"
import { useNavigate } from "react-router-dom"
import { deletePartnerUuidFleetsUuid, postPartnerUuidFleets } from "@src/api"
import { colors, config } from "@src/constants"
import { TrashFill } from "react-bootstrap-icons"
import { FLEETS } from "./types"
import { colorTagStyle } from "@src/utils/style"

export const FleetsScreen: React.FC = () => {
  const { partner } = usePartner()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { fleets, updateFleets } = useFleets()
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [fleetName, setFleetName] = useState<string>("")
  const [buttonLoader, setButtonLoader] = useState(false)
  const [deleteLoader, setDeleteLoader] = useState(false)
  const [selectedFleet, setSelectedFleet] = useState<FLEETS>()
  const [error, setError] = useState<boolean>(false)
  const showCreateFleet = fleets?.length < config.partnerFleetsItemsPerPage

  const onInputPersist = (field: string) => (text: string) => {
    setFleetName(text)
  }

  useEffect(() => {
    updateFleets()
  }, [])

  const createFleet = () => {
    if (fleetName.trim() !== "") {
      setError(false)
      setButtonLoader(true)
      postPartnerUuidFleets(partner.uuid, {
        fleet: {
          name: fleetName,
          contact_attributes: null,
        },
      }).then(({ response, data }) => {
        setButtonLoader(false)
        setShowModal(false)
        if (response.ok) {
          updateFleets()
          navigate(`/${partner.uuid}/settings/fleets/${data.fleet.uuid}`)
        }
      })
    } else {
      setError(true)
    }
  }

  const onClickDelete = (fleet: FLEETS) => {
    setDeleteLoader(true)
    setShowDeleteModal(false)
    deletePartnerUuidFleetsUuid(partner.uuid, fleet.uuid).then(({ response, data }) => {
      if (response.ok) {
        setDeleteLoader(false)
        updateFleets()
      }
    })
  }

  const renderFleetsCard = (fleet: FLEETS, i: number) => {
    return (
      <ListGroupItem>
        <div className="d-flex align-items-center pointer">
          <Col
            onClick={() => {
              navigate(`/${partner.uuid}/settings/fleets/${fleet.uuid}`)
            }}
          >
            <b>{fleet.name.charAt(0).toUpperCase() + fleet.name.slice(1)}</b>
          </Col>
          <b className="small me-3">
            ({fleet.devices_count} {t("sidebar.devices")})
          </b>
          <Button
            disabled={selectedFleet && selectedFleet.uuid == fleet.uuid && deleteLoader}
            className="align-self-end"
            variant="danger"
            onClick={() => {
              setSelectedFleet(fleet)
              setShowDeleteModal(true)
            }}
          >
            {selectedFleet && selectedFleet.uuid == fleet.uuid && deleteLoader ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <TrashFill color={colors.white} />
            )}
          </Button>
        </div>
      </ListGroupItem>
    )
  }

  return (
    <Page isSettings showHeader headerValue={t("settings.sidebar.fleets")}>
      <ListGroup>
        {showCreateFleet && (
          <ListGroup.Item
            action
            onClick={() => {
              setError(false)
              setShowModal(true)
            }}
          >
            <b>{t("settings.fleets.newFleet")}</b>
          </ListGroup.Item>
        )}
        <>
          {fleets.length == 0 ? (
            <ListGroup.Item>{t("settings.fleets.no_fleet")}</ListGroup.Item>
          ) : (
            <>{fleets.map((fleet, i) => renderFleetsCard(fleet, i))}</>
          )}
        </>
      </ListGroup>
      <ModalView
        title={t("settings.fleets.createFleet")}
        buttonTitle={t("settings.fleets.create")}
        show={showModal}
        buttonLoader={buttonLoader}
        onHideModal={() => setShowModal(false)}
        onButtonClick={createFleet}
      >
        <Col xs={12} md={6} lg={6}>
          <InputField
            label={t("settings.fleets.fleetName")}
            value={""}
            onPersist={onInputPersist("name")}
            onChangeText={() => setError(false)}
            loading={false}
          />
          {error && (
            <label className="small d-flex mb-2" style={colorTagStyle(colors.red)}>
              {t("settings.fleets.validNewFleetError")}
            </label>
          )}
        </Col>
      </ModalView>
      <ModalView
        title={t("settings.fleets.alert")}
        buttonTitle={t("settings.fleets.delete")}
        show={showDeleteModal}
        buttonLoader={deleteLoader}
        onHideModal={() => setShowDeleteModal(false)}
        onButtonClick={() => onClickDelete(selectedFleet)}
      >
        <label>{t("settings.fleets.areYouSure")}</label>
      </ModalView>
    </Page>
  )
}
